import { Button, Flex, Tag, Text } from '@chakra-ui/react';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { DataTable } from 'components/Table/DataTable';
import { useUserContext } from 'context/UserContextProvider';
import { format, startOfDay } from 'date-fns';
import { useLegalDocumentsQuery } from 'hooks/useLegalDocumentsQuery';
import { ContentCard } from 'layout/ContentCard';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SelectColumnFilter } from '../../components/Table/Filter';

const LegalDocumentsOverview = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const [documentTypeFilter, setDocumentTypeFilter] = useState<'tos' | 'dpp'>();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const legalDocumentsQuery = useLegalDocumentsQuery({
    page,
    itemsPerPage,
    type: documentTypeFilter,
  });

  const formatDate = (date: string) => {
    return format(startOfDay(new Date(date)), 'MM-yyyy');
  };

  const legalDocuments = legalDocumentsQuery?.data?.data || [];

  const columns = useMemo(() => {
    const statusTagColor = {
      outdated: 'gray',
      planned: 'blue',
      enrolled: 'green',
    } as const;
    const typeDropdownOptions = [
      {
        value: '',
        label: t('legal_documents:legal_document_properties.types.all'),
      },
      {
        value: 'dpp',
        label: t('legal_documents:legal_document_properties.types.dpp'),
      },
      {
        value: 'tos',
        label: t('legal_documents:legal_document_properties.types.tos'),
      },
    ];

    return [
      {
        Header: t('legal_documents:legal_document_properties.status'),
        accessor: 'status',
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          const status = row.original.status as keyof typeof statusTagColor;
          return (
            <Tag colorScheme={statusTagColor[status]} variant={status != 'outdated' ? 'subtle' : 'outline'}>
              {t(`legal_documents:legal_document_properties.statuses.${status}`)}
            </Tag>
          );
        },
      },
      {
        Header: t('legal_documents:legal_document_properties.type'),
        accessor: 'type',
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          const type = row.original.type;
          return <Text>{t(`legal_documents:legal_document_properties.types.${type}`)}</Text>;
        },
        shouldFilter: true,
        // @ts-ignore
        Filter: ({ column }) =>
          SelectColumnFilter({
            filterValue: documentTypeFilter,
            column,
            dropdownOptions: typeDropdownOptions,
            onChange: setDocumentTypeFilter,
          }),
      },
      {
        Header: t('legal_documents:legal_document_properties.version'),
        accessor: 'version',
      },
      {
        Header: t('legal_documents:legal_document_properties.effective_at'),
        accessor: (row: { effective_at: string }) => formatDate(row.effective_at) ?? '-',
      },
      {
        accessor: 'id',
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          return (
            <Flex justifyContent="flex-end">
              <Button as={Link} to={`/app/op/legal-document-version/${row.original.id}`}>
                {t('legal_documents:legal_documents_overview.link_button')}
              </Button>
            </Flex>
          );
        },
      },
    ];
  }, [documentTypeFilter, t]);

  if (legalDocumentsQuery.isPending) {
    return <LoadingSpinner />;
  }

  return (
    <ContentCard header={t('legal_documents:legal_documents_overview.title')}>
      {user.permissions.includes('legal-document-versions:create') && (
        <Button variant="primary" as={Link} to="/app/op/legal-document-version/create" mb="8" width="min-content">
          {t('legal_documents:legal_documents_overview.button')}
        </Button>
      )}
      <DataTable
        data-test-id="legal-documents-table"
        data={legalDocuments}
        resetFilter={() => setDocumentTypeFilter(undefined)}
        hasFilters
        columns={columns}
        loading={legalDocumentsQuery.isFetching}
        isMoreThenOnePage
        hasSearchInput={false}
        currentPage={page - 1}
        setPage={setPage}
        perPage={itemsPerPage}
        setPerPage={setItemsPerPage}
        total={Math.ceil((legalDocumentsQuery.data?.meta?.total ?? 0) / (legalDocumentsQuery.data?.meta?.per_page ?? 1))}
      />
    </ContentCard>
  );
};

export default LegalDocumentsOverview;
